import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { ArrowRight, Globe, Star, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function HeroSection() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        bgcolor: "#FAFAFA",
        color: "#111827",
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        pt: { xs: 16, md: 24 },
        pb: { xs: 12, md: 20 },
      }}
    >
      {/* Premium Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-[#d15424] opacity-[0.15] blur-[100px]"></div>
      </div>

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 10 }}>
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-12 items-center justify-between">
            
            {/* Left Column: Text & CTA */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="w-full lg:w-[55%] max-w-3xl"
            >
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-white rounded-full shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100 mb-8 hover:shadow-[0_4px_20px_rgba(209,84,36,0.1)] transition-all">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 text-[#d15424]">
                        <Globe size={14} />
                    </span>
                    <span className="text-xs font-bold text-gray-800 font-montserrat tracking-wide uppercase">Global Tech Academy</span>
                </div>

                <Typography
                    variant="h1"
                    sx={{
                        fontSize: { xs: "3rem", md: "4.5rem", lg: "5rem" },
                        fontWeight: 900,
                        lineHeight: 1.05,
                        mb: 4,
                        fontFamily: "Monserrat, sans-serif",
                        color: "#0f172a",
                        letterSpacing: "-0.03em"
                    }}
                >
                    Master the skills <br className="hidden md:block" />
                    that build the <span className="text-[#d15424] relative inline-block">
                        Future.
                        <svg className="absolute w-full h-4 -bottom-2 left-0 text-[#d15424]/20" viewBox="0 0 100 10" preserveAspectRatio="none"><path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" /></svg>
                    </span>
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: "1.1rem", md: "1.35rem" },
                        color: "#475569",
                        mb: 8,
                        lineHeight: 1.6,
                        fontFamily: "Monserrat, sans-serif",
                        maxWidth: "95%",
                        fontWeight: 500
                    }}
                >
                    Join a world-class immersive learning experience. Build real-world products, get mentored by industry leaders, and launch a high-impact global career.
                </Typography>

                <div className="flex flex-col sm:flex-row items-center gap-5">
                    <button
                        onClick={() => navigate("/sign-up")}
                        className="w-full sm:w-auto px-8 py-4 bg-[#d15424] text-white font-bold rounded-xl shadow-[0_8px_20px_rgba(209,84,36,0.25)] hover:shadow-[0_15px_30px_rgba(209,84,36,0.35)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 font-montserrat text-lg border border-transparent"
                    >
                        Apply for Next Cohort
                        <ArrowRight size={20} />
                    </button>
                    <button
                        onClick={() => navigate("/courses")}
                        className="w-full sm:w-auto px-8 py-4 bg-white text-[#0f172a] font-bold rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] border border-gray-200 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 font-montserrat text-lg"
                    >
                        Explore Programs
                    </button>
                </div>

                <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6">
                    <div className="flex -space-x-4">
                        {[1, 2, 3, 4].map((i) => (
                            <img key={i} src={`https://i.pravatar.cc/100?img=${i + 15}`} alt="Student" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
                        ))}
                    </div>
                    <div>
                        <div className="flex items-center gap-1 mb-1">
                            {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />)}
                        </div>
                        <p className="text-sm font-bold text-gray-700 font-montserrat">Join 15,000+ global alumni</p>
                    </div>
                </div>

            </motion.div>

            {/* Right Column: Premium Imagery / Bento */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
                className="w-full lg:w-[45%] relative h-[500px] lg:h-[650px] flex items-center justify-center"
            >
                {/* Main Glass/Bento Image Container */}
                <div className="absolute inset-0 w-full h-full rounded-[2.5rem] overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.15)] border-[8px] border-white/60 bg-white/40 backdrop-blur-md z-10 p-2">
                    <div className="w-full h-full rounded-[2rem] overflow-hidden relative">
                        <img 
                            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                            alt="Global Tech Collaboration" 
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#111827]/60 via-transparent to-transparent"></div>
                    </div>
                </div>

                {/* Floating Premium Card 1 */}
                <motion.div 
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.7 }}
                    className="absolute -right-4 lg:-right-10 top-20 bg-white/95 backdrop-blur-xl p-5 rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.1)] border border-gray-100 flex items-center gap-4 z-20"
                >
                    <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                        <ShieldCheck size={24} />
                    </div>
                    <div>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2, fontFamily: "Monserrat, sans-serif" }}>Certified</Typography>
                        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, fontFamily: "Monserrat, sans-serif" }}>Global Standards</Typography>
                    </div>
                </motion.div>

                {/* Floating Premium Card 2 */}
                <motion.div 
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.9 }}
                    className="absolute -left-4 lg:-left-12 bottom-16 bg-[#111827] text-white p-6 rounded-2xl shadow-[0_25px_50px_rgba(0,0,0,0.25)] border border-gray-800 flex flex-col gap-2 z-20"
                >
                    <Typography variant="overline" sx={{ color: "#94a3b8", fontWeight: 700, letterSpacing: 1.5, fontFamily: "Monserrat, sans-serif" }}>Hiring Rate</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: "white", fontFamily: "Monserrat, sans-serif", display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        94<span className="text-xl text-[#d15424]">%</span>
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", fontFamily: "Monserrat, sans-serif" }}>Placed within 6 months</Typography>
                </motion.div>

            </motion.div>
        </div>
      </Container>
    </Box>
  );
}
